import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import CaseAuditLogsPage from './CaseAuditLogsPage';
import AgGridTable from '../../components/AgGridTable';

const ProviderDetailsPage = ({ providerId: propProviderId, isEmbedded = false, onClose }) => {
  const { providerId: paramProviderId } = useParams();
  const navigate = useNavigate();
  const providerId = propProviderId || paramProviderId;
  const [provider, setProvider] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedData, setRelatedData] = useState({ cases: [] });
  const [activeTab, setActiveTab] = useState('cases');
  const [isEditing, setIsEditing] = useState(false);
  const [editedProvider, setEditedProvider] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [saveMessage, setSaveMessage] = useState({ type: '', message: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch provider details
        const providerResponse = await adminApi.getProviderDetails(providerId);
        if (providerResponse.data.success) {
          setProvider(providerResponse.data.provider);
        } else {
          setError('Failed to load provider details');
          setIsLoading(false);
          return;
        }

        // Fetch provider cases
        try {
          const casesResponse = await adminApi.getProviderCases(providerId);
          if (casesResponse.data.success) {
            const cases = casesResponse.data.data || [];
            setRelatedData(prev => ({ ...prev, cases: cases }));
          } else {
            console.error('Failed to load provider cases:', casesResponse.data.message);
            // Don't fail the whole page even if cases fail to load, but log the error
          }
        } catch (err) {
          console.error('Error loading provider cases:', err);
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response && err.response.status === 403) {
          setError('Permission denied. This feature requires proper authentication and authorization.');
        } else {
          setError('Failed to load provider data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (providerId) {
      fetchData();
    }
  }, [providerId]);

  // Fetch specialties and health systems when entering edit mode
  useEffect(() => {
    const fetchEditData = async () => {
      if (isEditing) {
        try {
          // Fetch specialties
          const specialtiesResponse = await adminApi.getSpecialties();
          if (specialtiesResponse.data.success) {
            setSpecialties(specialtiesResponse.data.specialties || []);
          }
          
          // Fetch health systems
          const healthSystemsResponse = await adminApi.getHealthSystems();
          if (healthSystemsResponse.data.success) {
            setHealthSystems(healthSystemsResponse.data.health_systems || []);
          }
        } catch (err) {
          console.error('Error fetching edit data:', err);
        }
      }
    };

    fetchEditData();
  }, [isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedProvider({ ...provider });
    setSaveMessage({ type: '', message: '' }); // Clear any previous messages
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProvider(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name === 'specialties') {
      // Handle multiple select for specialties
      const selectedOptions = Array.from(e.target.selectedOptions);
      const selectedSpecialties = selectedOptions.map(option => ({
        id: option.getAttribute('data-id'),
        name: option.value
      }));
      setEditedProvider(prev => ({
        ...prev,
        specialties: selectedSpecialties
      }));
    } else if (name === 'health_system_id') {
      // Handle health system selection - value is the UUID id, not health_system_id
      const selectedHealthSystem = healthSystems.find(hs => hs.id === value);
      setEditedProvider(prev => ({
        ...prev,
        health_system_id: value, // Send UUID to backend
        health_system: selectedHealthSystem || null
      }));
    } else if (type === 'select-one' && name === 'is_active') {
      setEditedProvider(prev => ({
        ...prev,
        [name]: value === 'true'
      }));
    } else {
      setEditedProvider(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveMessage({ type: '', message: '' }); // Clear previous messages
      
      // Only send allowed fields to backend
      const allowedFields = {
        first_name: editedProvider.first_name,
        middle_name: editedProvider.middle_name,
        last_name: editedProvider.last_name,
        provider_type: editedProvider.provider_type,
        phone: editedProvider.phone,
        phone_2: editedProvider.phone_2,
        email: editedProvider.email,
        is_active: editedProvider.is_active,
        specialties: editedProvider.specialties,
        health_system_id: editedProvider.health_system_id
      };
      
      const response = await adminApi.updateProviderDetails(providerId, allowedFields);
      
      if (response.data.success) {
        setProvider(response.data.provider);
        setSaveMessage({ type: 'success', message: 'Provider updated successfully!' });
        // Auto-clear success message after 3 seconds
        setTimeout(() => {
          setSaveMessage({ type: '', message: '' });
          setIsEditing(false);
          setEditedProvider(null);
          // Refresh the page to update the provider list
          window.location.reload();
        }, 2000);
      } else {
        setSaveMessage({ 
          type: 'error', 
          message: 'Failed to update provider: ' + (response.data.message || response.data.error || 'Unknown error')
        });
      }
    } catch (err) {
      console.error('Error updating provider:', err);
      setSaveMessage({ 
        type: 'error', 
        message: 'Failed to update provider: ' + (err.response?.data?.error || err.message || 'Network error')
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewCaseDetails = (caseId) => {
    setSelectedCaseId(caseId);
    setActiveTab('case-details');
  };

  const handleBackToCases = () => {
    setSelectedCaseId(null);
    setActiveTab('cases');
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error</h4>
        <p>{error}</p>
        {!isEmbedded && (
          <Link to="/admin/providers" className="btn btn-primary">
            Back to Providers
          </Link>
        )}
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="alert alert-warning" role="alert">
        <h4 className="alert-heading">Provider Not Found</h4>
        <p>The requested provider could not be found.</p>
        {!isEmbedded && (
          <Link to="/admin/providers" className="btn btn-primary">
            Back to Providers
          </Link>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Save Message */}
      {saveMessage.message && (
        <div className="row mb-3">
          <div className="col-12">
            <div className={`alert ${saveMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
              {saveMessage.message}
            </div>
          </div>
        </div>
      )}
      
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex card-title  justify-content-between align-items-center mb-3">
                <h5 className="text-primary mb-0">
                  {provider.first_name} {provider.middle_name ? provider.middle_name + ' ' : ''}{provider.last_name}
                </h5>
                <div>
                  {!isEditing ? (
                    <>
                      <button className="btn btn-primary btn-sm" onClick={handleEdit}>
                        <i className="far fa-edit"></i> Edit </button>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="provider-details">
                {!isEditing ? (
                  // Display mode
                  <>
                    <div className="row">
                      <div className="col-4">
                        <h5 className="font-size-15">UUID:</h5>
                        <p className="text-muted mb-0 small">{provider.id || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Provider ID:</h5>
                        <p className="text-muted mb-0">{provider.provider_id || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Novu Subscriber ID:</h5>
                        <p className="text-muted mb-0 small">{provider.novu_subscriber_id || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-4">
                        <h5 className="font-size-15">First Name:</h5>
                        <p className="text-muted mb-0">{provider.first_name || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Middle Name:</h5>
                        <p className="text-muted mb-0">{provider.middle_name || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Last Name:</h5>
                        <p className="text-muted mb-0">{provider.last_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-4">
                        <h5 className="font-size-15">Email:</h5>
                        <p className="text-muted mb-0">{provider.email || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Phone:</h5>
                        <p className="text-muted mb-0">{provider.phone || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Phone 2:</h5>
                        <p className="text-muted mb-0">{provider.phone_2 || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-4">
                        <h5 className="font-size-15">Status:</h5>
                        <p className="mb-0">
                          <span className={`badge ${provider.is_active ? 'bg-success' : 'bg-danger'}`}>
                            {provider.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Created At:</h5>
                        <p className="text-muted mb-0">{new Date(provider.created_at).toLocaleString()}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Updated At:</h5>
                        <p className="text-muted mb-0">{new Date(provider.updated_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-4">
                        <h5 className="font-size-15">Provider Type:</h5>
                        <p className="text-muted mb-0">{provider.provider_type || 'N/A'}</p>
                      </div>
                      <div className="col-4">
                        <h5 className="font-size-15">Specialties:</h5>
                        <p className="text-muted mb-0">
                          {provider.specialties?.length > 0 ? (
                            provider.specialties.map(s => (
                              <span key={s.id} className="badge bg-info me-1">
                                {s.name}
                              </span>
                            ))
                          ) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-4">
                        <h5 className="font-size-15">Health System:</h5>
                        <p className="text-muted mb-0">
                          {provider.health_system ? (
                            <>
                              <strong>Name:</strong> {provider.health_system.name}<br/>
                              <strong>ID:</strong> {provider.health_system.health_system_id || provider.health_system.id}
                            </>
                          ) : (
                            <span className="text-warning">No health system assigned</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  // Edit mode
                  <>
                    <div className="row">
                      <div className="col-6">
                        <label className="form-label">First Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          name="first_name"
                          value={editedProvider?.first_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Last Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          name="last_name"
                          value={editedProvider?.last_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-6">
                        <label className="form-label">Middle Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          name="middle_name"
                          value={editedProvider?.middle_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Provider Type:</label>
                        <input
                          type="text"
                          className="form-control"
                          name="provider_type"
                          value={editedProvider?.provider_type || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-12">
                        <label className="form-label">Specialties:</label>
                        <select
                          className="form-select"
                          name="specialties"
                          multiple
                          value={editedProvider?.specialties?.map(s => s.name) || []}
                          onChange={handleInputChange}
                          style={{ minHeight: '100px' }}
                        >
                          {specialties.map(s => (
                            <option key={s.id} value={s.name} data-id={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-6">
                        <label className="form-label">Email:</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={editedProvider?.email || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Phone:</label>
                        <input
                          type="tel"
                          className="form-control"
                          name="phone"
                          value={editedProvider?.phone || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-6">
                        <label className="form-label">Alternate Phone:</label>
                        <input
                          type="tel"
                          className="form-control"
                          name="phone_2"
                          value={editedProvider?.phone_2 || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Status:</label>
                        <select
                          className="form-select"
                          name="is_active"
                          value={editedProvider?.is_active}
                          onChange={handleInputChange}
                        >
                          <option value={true}>Active</option>
                          <option value={false}>Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-12">
                        <label className="form-label">Health System:</label>
                        <select
                          className="form-select"
                          name="health_system_id"
                          value={editedProvider?.health_system?.id || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Health System...</option>
                          {healthSystems.map(hs => (
                            <option key={hs.id} value={hs.id}>
                              {hs.name}
                            </option>
                          ))}
                        </select>
                        {!editedProvider?.health_system && (
                          <small className="text-muted">Provider currently has no health system assigned</small>
                        )}
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-12">
                        <button className="btn btn-primary me-2" onClick={handleSave} disabled={isSaving} >
                          {isSaving ? ( <> <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Saving... </> ) : ( <> Save </> )}
                        </button>
                        <button className="btn btn-secondary" onClick={handleCancel} disabled={isSaving} >Cancel </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Associated Health System Card */}
        {provider.health_system && (
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title text-primary mb-4">Associated Health System</h4>
                <p className="mb-0"><strong>Name:</strong> {provider.health_system.name}</p>
                <p className="mb-0"><strong>ID:</strong> {provider.health_system.id}</p>
                {/* Add more health system details if needed */}
              </div>
            </div>
          </div>
        )}

        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title text-primary mb-4">Cases ({relatedData.cases?.length || 0})</h4>

              {/* Tab Content */}
              <div className="tab-content mt-3">
                <div className={`tab-pane fade ${activeTab === 'cases' ? 'show active' : ''}`}>
                  {relatedData.cases?.length > 0 ? (
                    <AgGridTable
                      rowData={relatedData.cases}
                      height="100%"
                      columnDefs={[
                        {
                          headerName: 'Case ID',
                          field: 'CoreServer_CaseID',
                          width: 150
                        },
                        {
                          headerName: 'Patient Name',
                          field: 'patient_info_fk',
                          flex: 1,
                          valueGetter: (p) => {
                            if (!p.data?.patient_info_fk) return 'N/A';
                            const patient = p.data.patient_info_fk;
                            const name = `${patient.last_name || 'N/A'}, ${patient.first_name || 'N/A'} ${patient.middle_name || ''}`;
                            const dob = patient.dob ? new Date(patient.dob).toLocaleDateString() : 'No DOB found';
                            return `${name} (DOB: ${dob})`;
                          }
                        },
                        {
                          headerName: 'Status',
                          field: 'mira_sol_case_status',
                          width: 120,
                          cellRenderer: (params) => {
                            if (!params.data) return null;
                            const status = params.data.mira_sol_case_status;
                            const badgeClass = status === 'active' ? 'bg-success' : 'bg-secondary';
                            return (
                              <span className={`badge ${badgeClass}`}>
                                {status}
                              </span>
                            );
                          }
                        },
                        {
                          headerName: 'Created',
                          field: 'created_at',
                          width: 180,
                          valueGetter: (p) => {
                            if (!p.data?.created_at) return '';
                            return new Date(p.data.created_at).toLocaleString('en-US', {
                              year: 'numeric', month: '2-digit', day: '2-digit',
                              hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                            });
                          }
                        },
                        {
                          headerName: 'Actions',
                          field: 'actions',
                          width: 100,
                          sortable: false,
                          filter: false,
                          cellRenderer: (params) => {
                            if (!params.data) return null;
                            return (
                              <button
                                className="btn btn-info btn-sm"
                                onClick={() => handleViewCaseDetails(params.data.id)}
                              >
                                View
                              </button>
                            );
                          }
                        }
                      ]}
                      defaultColDef={{
                        resizable: true,
                        sortable: true,
                        filter: true,
                      }}
                      gridOptions={{
                        domLayout: 'autoHeight',
                        autoSizeStrategy: { type: 'fitGridWidth' }
                      }}
                      getRowId={(params) => `${params.data.id}`}
                    />
                  ) : (
                    <div className="text-center text-muted py-3">No cases found for this provider</div>
                  )}
                </div>
                
                <div className={`tab-pane fade ${activeTab === 'case-details' ? 'show active' : ''}`}>
                  {selectedCaseId && (
                    <>
                      <div className="d-flex justify-content-end align-items-center mb-3">
                        {/* <h5>Case Details - {selectedCaseId}</h5> */}
                        <button className="btn btn-secondary btn-sm" onClick={handleBackToCases}>Close</button>
                      </div>
                      <CaseAuditLogsPage caseId={selectedCaseId} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProviderDetailsPage;
